// THE VIEW-STATE RULES (spec 16 §3.12 items 5 to 9, D-305 rulings 6, 7 and 17
// to 21), as pure functions over a screen's URL state. Nothing here renders,
// fetches or navigates: the controller (`../hooks/use-saved-views.ts`) reads
// these to decide what to write, and `view-state.test.ts` pins every rule on
// its own.
//
// A SCREEN'S STATE, as this file sees it, is the validated URL search the
// screen already owns. Three kinds of parameter live in it:
//
//   * the FILTERS, the keys the screen names in `filterKeys`, which a view's
//     `config.filters` stores under the same keys (the contracts registry
//     keys a view's filters by the screen's URL keys);
//   * the ORDER, one parameter (`order` on the sales books, `sort` on the
//     work-order list) holding the screen's own sort token;
//   * everything else: `view` itself, and the parameters a view never
//     touches, such as the work-order list's peg pair (ruling 18) and, since
//     P3-E2 PR E2-1, the side peek's `peek` on the sales lists (D-314 ruling
//     8). A chip press, All, Reset, a filter write and D-301's drop all keep
//     them, and no Save stores them, because none of them is a filter key.
//
// THREE SPELLINGS OF ONE ORDER. An absent order, a stored `order: null` and
// the declared default token name one list (items 5 and 6), so every
// comparison here reads them through `normalizeOrder`, which answers
// `undefined` for all three.
//
// A VIEW AS THIS READER CAN APPLY IT (item 7). A stored filter the screen no
// longer accepts is stale: it is dropped, and the switcher says so. A stored
// order is fenced when the screen's own validator drops it for this reader
// (D-301's verdict), when the server refused it and the screen remembers
// that, or when the page has rows and the column the order sorts by is not
// present on it (the payload's own evidence, which survives a reload). A
// fenced order is dropped in silence. `resolveView` does all of it once, and
// Modified, the lit chip and the order-token rule all compare against what it
// returns, never against the stored config.

import type { components } from "@assessly/api-client";
import type { ColumnConfigEntry, DataTableColumn } from "../../../../components/data-table";

export type SavedView = components["schemas"]["SavedView"];
export type SavedViewScreenKey = components["schemas"]["SavedViewScreenKey"];
export type SavedViewStoredConfig = components["schemas"]["SavedViewStoredConfig"];

/** A screen's URL search, read as a record. */
export type SearchRecord = Readonly<Record<string, unknown>>;

/** The URL parameter that names the active view on every adopting screen. */
export const VIEW_PARAM = "view";

/**
 * The side peek's URL parameter, `peek=<kind>:<uuid>` (spec 09 §4.15, D-314
 * rulings 2 and 8). It is screen state outside every view's grammar: no
 * adopting screen may name it as a filter key (`views-contract.test.ts` holds
 * that), so `nonFilterParams` carries it through every write this layer
 * makes, and a view never stores it.
 */
export const PEEK_PARAM = "peek";

/**
 * How many of the reader's own and shared views the chip row shows before
 * More views (D-305 ruling 21). System views and All are outside the budget.
 */
export const CHIP_BUDGET = 6;

/** What the rules need to know about one adopting screen. */
export interface ViewScreenSpec {
  readonly screenKey: SavedViewScreenKey;
  /** The screen's URL filter keys, the keys a view's `config.filters` holds. */
  readonly filterKeys: readonly string[];
  /** The URL key the screen's sort token rides on. */
  readonly orderParam: string;
  /** The token the list route applies when the order is absent. */
  readonly declaredDefaultOrder: string;
  /**
   * The screen's own URL validator, run with the reader's verdict (D-301), so
   * a money order a reader is known not to be sent is dropped here exactly as
   * it is dropped from a pasted address. It must drop what it cannot accept
   * and never throw.
   */
  readonly validate: (raw: Record<string, unknown>) => object;
}

/** What the reader's session knows that fences a stored order (item 7). */
export interface OrderFence {
  /** The server refused this view's order after the screen applied it, and
   * the running screen remembers that. */
  readonly refused: boolean;
  /** The loaded page has rows and the column this order sorts by is absent. */
  readonly absentColumn: boolean;
}

export const NO_FENCE: OrderFence = { refused: false, absentColumn: false };

/** A view as this reader can apply it. */
export interface AppliedView {
  /** The stored filters that survive the screen's validator, keyed by URL key. */
  readonly filters: Readonly<Record<string, unknown>>;
  /** The order this reader applies, `undefined` for the declared default. */
  readonly order: string | undefined;
  /** The stored columns, as stored. The table ignores what it cannot render. */
  readonly columns: readonly ColumnConfigEntry[];
  /** The stored filter keys the screen dropped: each one is stale. */
  readonly staleFilters: readonly string[];
}

export function asSearch(value: object): SearchRecord {
  return value as SearchRecord;
}

/**
 * A value the address writes as an absent parameter. `false` is NOT absent:
 * `credit_hold=false` is a filter the customer book honours.
 */
export function isAbsentValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * One stable spelling of a value, for comparisons. Object keys are sorted,
 * and a list is compared as the set it is, since every list filter an
 * adopting screen has (the work-order status list) is a set to its route.
 */
export function canonicalValue(value: unknown): string {
  if (Array.isArray(value)) {
    const parts = (value as readonly unknown[]).map(canonicalValue);
    parts.sort();
    return `[${parts.join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, member]) => member !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([key, member]) => `${JSON.stringify(key)}:${canonicalValue(member)}`).join(",")}}`;
  }
  if (value === undefined) return "undefined";
  return JSON.stringify(value);
}

/** The view the address names, if any. */
export function viewParamOf(search: SearchRecord): string | undefined {
  const value = search[VIEW_PARAM];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** The filters the state carries, keyed by URL key, absent ones left out. */
export function filtersOf(spec: ViewScreenSpec, search: SearchRecord): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of spec.filterKeys) {
    const value = search[key];
    if (!isAbsentValue(value)) out[key] = Array.isArray(value) ? [...(value as unknown[])] : value;
  }
  return out;
}

/** An order token read the one way every comparison reads it: absent, null
 * and the declared default all come back `undefined` (items 5 and 6). */
export function normalizeOrder(spec: ViewScreenSpec, token: unknown): string | undefined {
  if (typeof token !== "string" || token.length === 0) return undefined;
  return token === spec.declaredDefaultOrder ? undefined : token;
}

export function orderOf(spec: ViewScreenSpec, search: SearchRecord): string | undefined {
  return normalizeOrder(spec, search[spec.orderParam]);
}

export function sameFilters(
  a: Readonly<Record<string, unknown>>,
  b: Readonly<Record<string, unknown>>,
): boolean {
  return canonicalValue(a) === canonicalValue(b);
}

/** The parameters a view never touches: every key that is not a filter, not
 * the order and not `view` (on the work-order list the peg pair, and on the
 * sales lists `peek`, D-314 ruling 8). */
export function nonFilterParams(
  spec: ViewScreenSpec,
  search: SearchRecord,
): Record<string, unknown> {
  const skip = new Set<string>([...spec.filterKeys, spec.orderParam, VIEW_PARAM]);
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(search)) {
    if (!skip.has(key) && value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * The address carries `view` and no filter or sort parameter (item 5). Only
 * such an address applies the stored config on arrival, and only on such an
 * arrival does the list read wait for the views read.
 */
export function isViewAlone(spec: ViewScreenSpec, search: SearchRecord): boolean {
  return (
    viewParamOf(search) !== undefined &&
    Object.keys(filtersOf(spec, search)).length === 0 &&
    isAbsentValue(search[spec.orderParam])
  );
}

/**
 * Is the order fenced by the payload (item 7's presence arm)? The column that
 * sorts by the token's key declares `presence`, the page has rows, and the
 * column is absent from them. A page with no rows proves nothing, so the
 * order then stays in the comparison. A holder's rows always carry the field,
 * so this never fences a holder's order.
 */
export function orderFencedByPresence<Row, K extends string>(
  columns: readonly DataTableColumn<Row, K>[],
  rows: readonly Row[],
  token: string | null | undefined,
): boolean {
  if (typeof token !== "string" || token.length === 0 || rows.length === 0) return false;
  const base = token.startsWith("-") ? token.slice(1) : token;
  const column = columns.find((c) => c.sortKey === base);
  if (column?.presence === undefined) return false;
  return !column.presence(rows);
}

/**
 * The view as this reader can apply it (item 7). Stored filter keys the
 * screen does not have, and values its validator drops or changes, are stale.
 * The stored order passes through the same validator, then the two fence arms
 * the validator cannot see; a dropped order is the declared default.
 */
export function resolveView(
  spec: ViewScreenSpec,
  config: SavedViewStoredConfig,
  fence: OrderFence,
): AppliedView {
  const filterKeys = new Set(spec.filterKeys);
  const stored = config.filters;
  const raw: Record<string, unknown> = {};
  const staleFilters: string[] = [];
  for (const [key, value] of Object.entries(stored)) {
    if (filterKeys.has(key)) raw[key] = value;
    else staleFilters.push(key);
  }
  const storedOrder = typeof config.order === "string" ? config.order : undefined;
  if (storedOrder !== undefined) raw[spec.orderParam] = storedOrder;

  const validated = asSearch(spec.validate({ ...raw }));
  const filters = filtersOf(spec, validated);
  for (const key of Object.keys(raw)) {
    if (key === spec.orderParam) continue;
    if (canonicalValue(raw[key]) !== canonicalValue(filters[key])) staleFilters.push(key);
  }
  const fenced = fence.refused || fence.absentColumn;
  const order = fenced ? undefined : orderOf(spec, validated);
  return { filters, order, columns: config.columns, staleFilters };
}

/** Do the state's filters and order equal the view's, as applied? */
export function stateMatchesView(
  spec: ViewScreenSpec,
  search: SearchRecord,
  applied: AppliedView,
): boolean {
  return (
    sameFilters(filtersOf(spec, search), applied.filters) && orderOf(spec, search) === applied.order
  );
}

/**
 * Modified (item 5): the address's filters and order, together with the
 * table's columns, differ from the view as this reader can apply it. The
 * caller compares the columns by what the table renders
 * (`sameColumnLayout`), never entry by entry, and passes the answer in.
 */
export function isViewModified(
  spec: ViewScreenSpec,
  search: SearchRecord,
  applied: AppliedView,
  columnsSame: boolean,
): boolean {
  return !(stateMatchesView(spec, search, applied) && columnsSame);
}

/**
 * The address a press of a view's chip writes (item 5): `view`, the view's
 * filters and its order, and every parameter that is not a filter. Every
 * filter the view does not name leaves the address, so a view that names no
 * customer clears a customer the picker had chosen (ruling 4). An order equal
 * to the declared default is written as an absent parameter.
 */
export function appliedAddress(
  spec: ViewScreenSpec,
  current: SearchRecord,
  viewId: string,
  applied: AppliedView,
): Record<string, unknown> {
  return {
    ...nonFilterParams(spec, current),
    [VIEW_PARAM]: viewId,
    ...applied.filters,
    ...(applied.order !== undefined ? { [spec.orderParam]: applied.order } : {}),
  };
}

/** The bare list (item 6): no `view`, no filter, no order, and every other
 * parameter kept. */
export function bareAddress(spec: ViewScreenSpec, current: SearchRecord): Record<string, unknown> {
  return nonFilterParams(spec, current);
}

/** The address with `view` removed and everything else kept (items 3 and 9). */
export function addressWithoutView(current: SearchRecord): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(current)) {
    if (key !== VIEW_PARAM && value !== undefined) out[key] = value;
  }
  return out;
}

/** The address with the order removed and everything else kept, `view` and
 * every filter included (D-301's fallback, ruling 7). */
export function addressWithoutOrder(
  spec: ViewScreenSpec,
  current: SearchRecord,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(current)) {
    if (key !== spec.orderParam && value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * THE ORDER-TOKEN WRITE RULE (item 5, ruling 17). It judges the address a
 * write produces. When that address holds `view`, carries no order, and its
 * filters and order differ from the view as applied, the write carries the
 * declared default token explicitly. When the view's config is unknown
 * (`applied` undefined: the views read has failed, is still in flight, or the
 * view is not in it), a difference cannot be judged, so the token goes in.
 * An address holding `view` alone therefore only ever means the view as
 * stored. An address without `view` is returned unchanged, so a bare address
 * stays byte-identical.
 */
export function withOrderToken(
  spec: ViewScreenSpec,
  next: SearchRecord,
  applied: AppliedView | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...next };
  if (viewParamOf(next) === undefined) return out;
  if (!isAbsentValue(next[spec.orderParam])) return out;
  if (applied !== undefined && stateMatchesView(spec, next, applied)) return out;
  out[spec.orderParam] = spec.declaredDefaultOrder;
  return out;
}

/** All is pressed when the address names no view, no filter, and either no
 * order or the declared default (item 6). */
export function isAllPressed(spec: ViewScreenSpec, search: SearchRecord): boolean {
  return (
    viewParamOf(search) === undefined &&
    Object.keys(filtersOf(spec, search)).length === 0 &&
    orderOf(spec, search) === undefined
  );
}

/**
 * Is a view's chip pressed (item 6, ruling 18)? While `view` names a view,
 * that view's chip, Modified or not. With no `view`, a system view's chip
 * when its filters AND its order equal the address's, so `?overdue=true`
 * presses Overdue and `?status=completed&sort=priority` presses nothing: the
 * list on screen is a different list. A user view is pressed only through
 * `view`, because two user views may hold one state.
 */
export function isChipPressed(
  spec: ViewScreenSpec,
  search: SearchRecord,
  view: SavedView,
  applied: AppliedView,
): boolean {
  const active = viewParamOf(search);
  if (active !== undefined) return active === view.id;
  return view.is_system && stateMatchesView(spec, search, applied);
}

/** The screen's views in the three groups the switcher draws. */
export interface ViewGroups {
  /** The system views, in the order their fixed ids give. */
  readonly system: readonly SavedView[];
  /** The reader's own views (`mutable`), most recently updated first. */
  readonly own: readonly SavedView[];
  /** Other members' shared views, most recently updated first. */
  readonly shared: readonly SavedView[];
}

function byRecentUpdate(a: SavedView, b: SavedView): number {
  if (a.updated_at !== b.updated_at) return a.updated_at < b.updated_at ? 1 : -1;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/** Sort the list read into its groups. A view keyed to another screen is
 * ignored, whatever the read returned. */
export function groupViews(items: readonly SavedView[], screenKey: SavedViewScreenKey): ViewGroups {
  const mine = items.filter((v) => v.screen_key === screenKey);
  const system = mine.filter((v) => v.is_system).sort((a, b) => (a.id < b.id ? -1 : 1));
  const own = mine.filter((v) => !v.is_system && v.mutable).sort(byRecentUpdate);
  const shared = mine.filter((v) => !v.is_system && !v.mutable).sort(byRecentUpdate);
  return { system, own, shared };
}

/** Every view in the chip row's order: what Manage and the apply-only list
 * show. */
export function allInRowOrder(groups: ViewGroups): SavedView[] {
  return [...groups.system, ...groups.own, ...groups.shared];
}

/** The chips the row shows and whether More views follows them. */
export interface ChipRow {
  readonly system: readonly SavedView[];
  readonly own: readonly SavedView[];
  readonly shared: readonly SavedView[];
  readonly more: boolean;
}

/**
 * THE CHIP BUDGET (ruling 21). All and every system view, then at most six
 * of the reader's own and shared views, own first and each group by most
 * recent update. More views follows when the reader has more. The active view
 * always has a chip: when it is not among the six it takes the sixth place,
 * the view that would have been sixth moves behind More views, and the six
 * show in the usual order, so the row never holds more than six.
 */
export function chipRow(groups: ViewGroups, activeId: string | undefined): ChipRow {
  const candidates = [...groups.own, ...groups.shared];
  let chosen = candidates.slice(0, CHIP_BUDGET);
  const active =
    activeId === undefined ? undefined : candidates.find((view) => view.id === activeId);
  if (active !== undefined && !chosen.includes(active)) {
    chosen = [...candidates.filter((view) => view !== active).slice(0, CHIP_BUDGET - 1), active];
  }
  const shown = new Set(chosen);
  return {
    system: groups.system,
    own: groups.own.filter((view) => shown.has(view)),
    shared: groups.shared.filter((view) => shown.has(view)),
    more: candidates.length > CHIP_BUDGET,
  };
}

/** The config a Save or a Save as new stores: the state's filters and order
 * and the table's current column entries (item 3). */
export interface ViewConfigBody {
  readonly v: 1;
  readonly filters: Record<string, unknown>;
  readonly order: string | null;
  readonly columns: ColumnConfigEntry[];
}

export function configFromState(
  spec: ViewScreenSpec,
  search: SearchRecord,
  columns: readonly ColumnConfigEntry[],
): ViewConfigBody {
  return {
    v: 1,
    filters: filtersOf(spec, search),
    order: orderOf(spec, search) ?? null,
    columns: columns.map((entry) => ({
      key: entry.key,
      ...(entry.hidden !== undefined ? { hidden: entry.hidden } : {}),
      ...(entry.width !== undefined ? { width: entry.width } : {}),
    })),
  };
}

/** A key that says which parts of the address the views logic reads, so two
 * addresses that differ only in parameters no view touches compare equal. */
export function addressKey(spec: ViewScreenSpec, search: SearchRecord): string {
  return canonicalValue({
    view: viewParamOf(search),
    filters: filtersOf(spec, search),
    order: orderOf(spec, search),
    explicitOrder: isAbsentValue(search[spec.orderParam]) ? undefined : search[spec.orderParam],
  });
}
